import React, { useState, useEffect } from 'react';
import './HistoryList.css';
import { getVisitorHistory, deleteVisitor } from '../../services/api';
import { formatDateTime } from '../../utils/dateHelpers';
import { getCookie, setCookie } from '../../utils/cookieHelpers';

function HistoryList() {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    // Initialize from cookie, default to 'table'
    return getCookie('historyViewMode') || 'table';
  });

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await getVisitorHistory(30);
      // Sort by check-in time, newest first
      const sorted = data.sort((a, b) =>
        new Date(b.check_in_time) - new Date(a.check_in_time)
      );
      setVisitors(sorted);
      setError('');
    } catch (err) {
      setError('Kunde inte hämta historik. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const toggleViewMode = () => {
    const newMode = viewMode === 'card' ? 'table' : 'card';
    setViewMode(newMode);
    setCookie('historyViewMode', newMode);
  };

  const handleDelete = async (visitor) => {
    const confirmMessage = `Är du säker på att du vill ta bort besöket för ${visitor.name}?`;

    if (window.confirm(confirmMessage)) {
      try {
        await deleteVisitor(visitor.id);
        // Refresh the list after deletion
        fetchHistory();
      } catch (err) {
        setError('Kunde inte ta bort besöket. Försök igen.');
      }
    }
  };

  if (loading) {
    return <div className="loading">Laddar historik...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchHistory} className="retry-button">
          Försök igen
        </button>
      </div>
    );
  }

  if (visitors.length === 0) {
    return (
      <div className="empty-state">
        <h2>Ingen besökshistorik</h2>
        <p>Det finns ingen besökshistorik för de senaste 30 dagarna.</p>
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-header">
        <h2>Besökshistorik (Senaste 30 dagarna)</h2>
        <button onClick={toggleViewMode} className="view-toggle-button">
          {viewMode === 'card' ? 'Visa tabell' : 'Visa kort'}
        </button>
      </div>

      {viewMode === 'card' ? (
        <div className="history-grid">
          {visitors.map(visitor => (
            <div
              key={visitor.id}
              className={`history-card ${visitor.is_overnight ? 'overnight' : ''} clickable`}
              onClick={() => handleDelete(visitor)}
            >
              <div className="history-card-header">
                <h3>{visitor.name}</h3>
              </div>
              <div className="history-card-content">
                <div className="history-info-row">
                  <span className="history-label">Företag:</span>
                  <span className="history-value">{visitor.company}</span>
                </div>
                <div className="history-info-row">
                  <span className="history-label">Ärende:</span>
                  <span className="history-value">{visitor.reason}</span>
                </div>
                <div className="history-info-row">
                  <span className="history-label">Telefon:</span>
                  <span className="history-value">{visitor.phone}</span>
                </div>
                <div className="history-times">
                  <div className="history-time">
                    <span className="time-label">Incheckning</span>
                    <span className="time-value">{formatDateTime(visitor.check_in_time)}</span>
                  </div>
                  <div className="history-time">
                    <span className="time-label">Utcheckning</span>
                    <span className={`time-value ${!visitor.check_out_time ? 'not-checked-out' : ''}`}>
                      {formatDateTime(visitor.check_out_time)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="history-table-wrapper">
          <table className="history-table">
            <thead>
              <tr>
                <th>Namn</th>
                <th>Företag</th>
                <th>Ärende</th>
                <th>Telefon</th>
                <th>Incheckning</th>
                <th>Utcheckning</th>
              </tr>
            </thead>
            <tbody>
              {visitors.map(visitor => (
                <tr
                  key={visitor.id}
                  className={`${visitor.is_overnight ? 'overnight-row' : ''} clickable-row`}
                  onClick={() => handleDelete(visitor)}
                >
                  <td>
                    <div className="table-name-cell">
                      {visitor.name}
                      {visitor.is_overnight && (
                        <span className="overnight-badge-small">Övernattande</span>
                      )}
                    </div>
                  </td>
                  <td>{visitor.company}</td>
                  <td>{visitor.reason}</td>
                  <td>{visitor.phone}</td>
                  <td>{formatDateTime(visitor.check_in_time)}</td>
                  <td className={!visitor.check_out_time ? 'not-checked-out' : ''}>
                    {formatDateTime(visitor.check_out_time)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default HistoryList;